/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import api, { formatApiErrorDetail } from "@/lib/api";
import MembershipCard from "./subscribe/MembershipCard";
import ArchiveCard from "./subscribe/ArchiveCard";
import StatusStrip from "./subscribe/StatusStrip";

export default function Subscribe() {
  const [sub, setSub] = useState(null);
  const [archive, setArchive] = useState(null);
  const [history, setHistory] = useState([]);
  // { plan: 'membership'|'archive'|'cancel-membership'|'cancel-archive' } | false
  const [busy, setBusy] = useState(false);
  const [pollMsg, setPollMsg] = useState("");
  const [err, setErr] = useState("");
  // promos[plan] = { code, percent_off, duration_months, ... } once applied.
  const [promos, setPromos] = useState({ membership: null, archive: null });
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const [m, a, h] = await Promise.all([
        api.get("/billing/me"),
        api.get("/billing/archive/me"),
        api.get("/billing/history"),
      ]);
      setSub(m.data);
      setArchive(a.data);
      setHistory(h.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPollMsg("Confirming your payment…");
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          setPollMsg("Payment confirmed. You're in.");
          await load();
          return;
        }
        if (data.status === "expired") {
          setPollMsg("Payment session expired.");
          return;
        }
        if (attempts >= 6) {
          setPollMsg("Still processing — refresh in a moment.");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setPollMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    };
    poll();
  }, [params, load]);

  const startCheckout = async (plan) => {
    setBusy({ plan });
    setErr("");
    try {
      const promo = promos[plan];
      const { data } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        plan,
        ...(promo ? { promo_code: promo.code } : {}),
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      // 100%-off promo path — backend has already activated the plan.
      if (data.promo_activated) {
        setPollMsg(
          data.message ||
            `Promo applied. ${plan === "membership" ? "Membership" : "Archive"} activated.`,
        );
        setPromos((p) => ({ ...p, [plan]: null }));
        await load();
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const applyPromo = (plan) => (data) =>
    setPromos((p) => ({ ...p, [plan]: data }));
  const clearPromo = (plan) => () =>
    setPromos((p) => ({ ...p, [plan]: null }));

  const cancelMembership = async () => {
    setBusy({ plan: "cancel-membership" });
    setErr("");
    try {
      await api.post("/billing/cancel");
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const cancelArchive = async () => {
    setBusy({ plan: "cancel-archive" });
    setErr("");
    try {
      await api.post("/billing/archive/cancel");
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!sub || !archive) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="pt-28 max-w-4xl mx-auto px-6 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
          Loading…
        </main>
      </div>
    );
  }

  const membershipActive = sub.status === "active";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subscribe-page">
      <Navbar />
      <main className="pt-24 lg:pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <Link
            to="/dashboard"
            className="text-sm text-slate-500 inline-flex items-center gap-1.5 hover:text-slate-700"
            data-testid="subscribe-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
          </Link>
          <div className="mt-3">
            <p className="text-sm uppercase tracking-[0.18em] text-sky-700 font-medium">
              Membership & Archive
            </p>
            <h1
              className="mt-1.5 text-3xl lg:text-4xl font-medium tracking-tight text-slate-900"
              data-testid="subscribe-heading"
            >
              Pick the plan that suits where you are right now.
            </h1>
            <p className="mt-2 text-base text-slate-600 max-w-2xl">
              All prices in AUD. Australian GST (10%) is added at checkout — what
              you see on the right of each price is the total you'll actually pay.
            </p>
          </div>

          {pollMsg && (
            <div
              className="mt-4 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-sm text-sky-900"
              data-testid="subscribe-poll"
            >
              {pollMsg}
            </div>
          )}
          {err && (
            <div
              className="mt-4 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
              data-testid="subscribe-error"
            >
              {err}
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <MembershipCard
              sub={sub}
              busy={busy}
              onCheckout={startCheckout}
              promo={promos.membership}
              onPromoApply={applyPromo("membership")}
              onPromoClear={clearPromo("membership")}
            />
            <ArchiveCard
              archive={archive}
              membershipActive={membershipActive}
              busy={busy}
              onCheckout={startCheckout}
              promo={promos.archive}
              onPromoApply={applyPromo("archive")}
              onPromoClear={clearPromo("archive")}
            />
          </div>

          <StatusStrip
            sub={sub}
            archive={archive}
            history={history}
            busy={busy}
            onCancelMembership={cancelMembership}
            onCancelArchive={cancelArchive}
          />
        </div>
      </main>
    </div>
  );
}
